import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { sendContactEmail } from "../../services/email_service";
import "./ContactForm.css";

/**
 * Section 03 — Contact professionnel.
 * Même service et même contrat d'envoi que le formulaire validé :
 * firstname, name, email, telephone, company, subject, message.
 * Le type de demande est partagé avec ContactTopics dans Contact.jsx.
 */
const SUBJECTS = Object.freeze(["demo", "devis", "partenariat", "autre"]);

const COPY = {
  fr: {
    eyebrow: "02 / ÉCRIRE À UNITECH",
    badge: "Contact professionnel",
    title: "Votre demande",
    intro: "Quelques informations suffisent pour commencer.",
    fullName: "Prénom et nom",
    fullNamePlaceholder: "Votre prénom et votre nom",
    company: "Entreprise",
    companyPlaceholder: "Nom de votre entreprise",
    email: "Email professionnel",
    emailPlaceholder: "vous@entreprise.fr",
    phone: "Téléphone",
    optional: "facultatif",
    phonePlaceholder: "Avec indicatif pays",
    subject: "Objet de votre demande",
    subjectPlaceholder: "Sélectionnez un sujet",
    subjectOptions: {
      demo: "Découvrir OPENlift",
      devis: "Devis Téléalarme ISS",
      partenariat: "Devenir partenaire",
      autre: "Autre demande"
    },
    message: "Votre message",
    messagePlaceholder: "Quelques mots sur votre besoin suffisent…",
    openliftExtra: "Constructeurs ou modèles utilisés",
    openliftPlaceholder: "Ex. : constructeur et famille de contrôleurs",
    openliftHelp: "Facultatif. Pour mieux préparer une démonstration adaptée à votre parc.",
    issExtra: "Nombre d'équipements concernés",
    issPlaceholder: "Ex. : 30",
    issHelp: "Facultatif. Une estimation suffit pour préparer l'étude.",
    consentIntro: "Confidentialité",
    submit: "Envoyer ma demande",
    submitting: "Envoi en cours…",
    helper: "Informations transmises uniquement pour traiter votre demande.",
    requiredNote: "* Champs obligatoires",
    success: "Merci, votre demande a bien été envoyée. Notre équipe va l'examiner et vous recontacter aux coordonnées indiquées.",
    failure: "L'envoi n'a pas abouti. Réessayez ou utilisez nos coordonnées ci-dessous.",
    validationSummary: "Vérifiez les champs signalés avant d'envoyer votre demande.",
    errors: {
      fullName: "Indiquez votre prénom et votre nom.",
      company: "Indiquez votre entreprise.",
      email: "Indiquez une adresse email valide.",
      subject: "Sélectionnez un sujet.",
      message: "Décrivez brièvement votre demande.",
      phone: "Le numéro de téléphone est trop long.",
      extra: "Cette information est trop longue.",
      consent: "Veuillez confirmer la prise de connaissance de la politique de confidentialité."
    },
    extraPayloadOpenlift: "Constructeurs ou modèles utilisés",
    extraPayloadIss: "Nombre d'équipements concernés"
  },
  en: {
    eyebrow: "02 / CONTACT UNITECH",
    badge: "Business enquiry",
    title: "Your enquiry",
    intro: "Just a few details to get started.",
    fullName: "First and last name",
    fullNamePlaceholder: "Your first and last name",
    company: "Company",
    companyPlaceholder: "Your company name",
    email: "Business email",
    emailPlaceholder: "you@company.com",
    phone: "Phone",
    optional: "optional",
    phonePlaceholder: "Include country code",
    subject: "Subject of your enquiry",
    subjectPlaceholder: "Choose a subject",
    subjectOptions: {
      demo: "Discover OPENlift",
      devis: "ISS emergency call quotation",
      partenariat: "Become a partner",
      autre: "Other enquiry"
    },
    message: "Your message",
    messagePlaceholder: "A few words about your requirements are enough…",
    openliftExtra: "Manufacturers or controller models",
    openliftPlaceholder: "E.g. manufacturer and controller family",
    openliftHelp: "Optional. Helps us prepare a fleet-specific demonstration.",
    issExtra: "Number of lifts concerned",
    issPlaceholder: "E.g. 30",
    issHelp: "Optional. An estimate is enough for the initial assessment.",
    consentIntro: "Privacy",
    submit: "Send my enquiry",
    submitting: "Sending…",
    helper: "Information supplied is used to handle your enquiry.",
    requiredNote: "* Required fields",
    success: "Thank you, your enquiry has been sent. Our team will review it and contact you using the details provided.",
    failure: "Your enquiry could not be sent. Please try again or use the contact details below.",
    validationSummary: "Please check the highlighted fields before sending your enquiry.",
    errors: {
      fullName: "Please enter your first and last name.",
      company: "Please enter your company.",
      email: "Please enter a valid email address.",
      subject: "Please choose a subject.",
      message: "Please describe your enquiry briefly.",
      phone: "The phone number is too long.",
      extra: "This information is too long.",
      consent: "Please acknowledge the privacy notice."
    },
    extraPayloadOpenlift: "Manufacturers or controller models",
    extraPayloadIss: "Number of lifts concerned"
  }
};

const INITIAL_VALUES = Object.freeze({
  fullName: "",
  company: "",
  email: "",
  phone: "",
  message: "",
  extra: "",
  consent: false
});

function validate(values, subject, copy) {
  const errors = {};
  const name = values.fullName.trim();
  const company = values.company.trim();
  const email = values.email.trim();
  const message = values.message.trim();
  if (name.length > 160 || name.split(/\s+/).filter(Boolean).length < 2) errors.fullName = copy.errors.fullName;
  if (!company || company.length > 160) errors.company = copy.errors.company;
  // Validate format only: personal email domains are not rejected.
  if (email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = copy.errors.email;
  if (values.phone.trim().length > 40) errors.phone = copy.errors.phone;
  if (!SUBJECTS.includes(subject)) errors.subject = copy.errors.subject;
  if (!message || message.length > 4000) errors.message = copy.errors.message;
  if (values.extra.trim().length > 160) errors.extra = copy.errors.extra;
  if (!values.consent) errors.consent = copy.errors.consent;
  return errors;
}

export default function ContactForm({ selectedSubject = "", onSubjectChange }) {
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase().startsWith("en") ? "en" : "fr";
  const copy = COPY[language];
  const [values, setValues] = useState({ ...INITIAL_VALUES });
  const [touched, setTouched] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [status, setStatus] = useState("idle");
  const requestInFlight = useRef(false);
  const inputRefs = useRef({});
  const previousSubject = useRef(selectedSubject);
  const skipNextTopicReset = useRef(false);

  // Changing topic through cards or the select clears irrelevant extra details.
  useEffect(() => {
    if (previousSubject.current !== selectedSubject) {
      if (skipNextTopicReset.current) {
        skipNextTopicReset.current = false;
        previousSubject.current = selectedSubject;
        return;
      }
      setValues((current) => ({ ...current, extra: "" }));
      setTouched((current) => ({ ...current, extra: false }));
      previousSubject.current = selectedSubject;
      setStatus((current) => current === "sending" ? current : "idle");
    }
  }, [selectedSubject]);

  const errors = useMemo(() => validate(values, selectedSubject, copy), [values, selectedSubject, copy]);
  const extraIsShown = selectedSubject === "demo" || selectedSubject === "devis";

  function update(name, value) {
    setValues((current) => ({ ...current, [name]: value }));
    setStatus((current) => current === "sending" ? current : "idle");
  }

  function selectSubject(nextSubject) {
    onSubjectChange(nextSubject);
    setValues((current) => ({ ...current, extra: "" }));
    setTouched((current) => ({ ...current, extra: false }));
    setStatus((current) => current === "sending" ? current : "idle");
  }

  function markTouched(name) {
    setTouched((current) => ({ ...current, [name]: true }));
  }

  function describe(name, hint = false) {
    const ids = [];
    if (hint) ids.push(`contact-form-${name}-hint`);
    if ((touched[name] || showErrors) && errors[name]) ids.push(`contact-form-${name}-error`);
    return ids.length ? ids.join(" ") : undefined;
  }

  function errorFor(name) {
    return (touched[name] || showErrors) && errors[name] ? (
      <span id={`contact-form-${name}-error`} className="contact-form__error">{errors[name]}</span>
    ) : null;
  }

  function commonInput(name, type = "text") {
    return {
      id: `contact-form-${name}`,
      name,
      type,
      value: values[name],
      onChange: (event) => update(name, event.target.value),
      onBlur: () => markTouched(name),
      "aria-invalid": Boolean((touched[name] || showErrors) && errors[name]),
      "aria-describedby": describe(name),
      ref: (node) => { inputRefs.current[name] = node; },
      disabled: status === "sending"
    };
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (requestInFlight.current) return;
    setShowErrors(true);
    setStatus("idle");
    const currentErrors = validate(values, selectedSubject, copy);
    if (Object.keys(currentErrors).length) {
      const first = ["fullName", "company", "email", "phone", "subject", "extra", "message", "consent"]
        .find((name) => Boolean(currentErrors[name]));
      inputRefs.current[first]?.focus();
      return;
    }

    const parts = values.fullName.trim().split(/\s+/);
    const firstname = parts.shift();
    const name = parts.join(" ");
    const extraLabel = selectedSubject === "demo" ? copy.extraPayloadOpenlift : copy.extraPayloadIss;
    const message = extraIsShown && values.extra.trim()
      ? `${extraLabel}: ${values.extra.trim()}\n\n${values.message.trim()}`
      : values.message.trim();

    // Historical payload and email_service contract are unchanged.
    const payload = {
      firstname,
      name,
      email: values.email.trim(),
      telephone: values.phone.trim() || undefined,
      company: values.company.trim(),
      subject: selectedSubject,
      message
    };

    requestInFlight.current = true;
    setStatus("sending");
    try {
      await sendContactEmail(payload);
      setStatus("success");
      setValues({ ...INITIAL_VALUES });
      setTouched({});
      setShowErrors(false);
      skipNextTopicReset.current = true;
      onSubjectChange("");
    } catch (error) {
      // Do not log the contact payload (personal data).
      setStatus("error");
    } finally {
      requestInFlight.current = false;
    }
  }

  return (
    <section className="contact-form" id="contact-formulaire" aria-labelledby="contact-form-title">
      <div className="contact-form__topline">
        <span className="contact-form__eyebrow">{copy.eyebrow}</span>
        <span className="contact-form__badge">{copy.badge}</span>
      </div>
      <h2 className="contact-form__title" id="contact-form-title">{copy.title}</h2>
      <p className="contact-form__intro">{copy.intro}</p>

      {status === "success" && (
        <div className="contact-form__notice contact-form__notice--success" role="status" aria-live="polite">
          {copy.success}
        </div>
      )}
      {status === "error" && (
        <div className="contact-form__notice contact-form__notice--error" role="alert">
          {copy.failure}
        </div>
      )}
      {showErrors && Object.keys(errors).length > 0 && (
        <p className="contact-form__validation" role="alert">{copy.validationSummary}</p>
      )}

      <form className="contact-form__form" onSubmit={handleSubmit} noValidate>
        <div className="contact-form__twoCol">
          <div className="contact-form__field">
            <label htmlFor="contact-form-fullName">{copy.fullName} <span className="contact-form__required" aria-hidden="true">*</span></label>
            <input {...commonInput("fullName")} placeholder={copy.fullNamePlaceholder} autoComplete="name" maxLength={160} required />
            {errorFor("fullName")}
          </div>
          <div className="contact-form__field">
            <label htmlFor="contact-form-company">{copy.company} <span className="contact-form__required" aria-hidden="true">*</span></label>
            <input {...commonInput("company")} placeholder={copy.companyPlaceholder} autoComplete="organization" maxLength={160} required />
            {errorFor("company")}
          </div>
          <div className="contact-form__field">
            <label htmlFor="contact-form-email">{copy.email} <span className="contact-form__required" aria-hidden="true">*</span></label>
            <input {...commonInput("email", "email")} placeholder={copy.emailPlaceholder} autoComplete="email" maxLength={254} required />
            {errorFor("email")}
          </div>
          <div className="contact-form__field">
            <label htmlFor="contact-form-phone">{copy.phone} <span className="contact-form__optional">({copy.optional})</span></label>
            <input {...commonInput("phone", "tel")} placeholder={copy.phonePlaceholder} autoComplete="tel" maxLength={40} />
            {errorFor("phone")}
          </div>
        </div>

        <div className="contact-form__field">
          <label htmlFor="contact-form-subject">{copy.subject} <span className="contact-form__required" aria-hidden="true">*</span></label>
          <select
            id="contact-form-subject"
            name="subject"
            value={selectedSubject}
            onChange={(event) => selectSubject(event.target.value)}
            onBlur={() => markTouched("subject")}
            ref={(node) => { inputRefs.current.subject = node; }}
            disabled={status === "sending"}
            aria-invalid={Boolean((touched.subject || showErrors) && errors.subject)}
            aria-describedby={describe("subject")}
            required
          >
            <option value="">{copy.subjectPlaceholder}</option>
            {SUBJECTS.map((value) => <option key={value} value={value}>{copy.subjectOptions[value]}</option>)}
          </select>
          {errorFor("subject")}
        </div>

        {extraIsShown && (
          <div className="contact-form__field">
            <label htmlFor="contact-form-extra">
              {selectedSubject === "demo" ? copy.openliftExtra : copy.issExtra}
              <span className="contact-form__optional"> ({copy.optional})</span>
            </label>
            <input
              {...commonInput("extra")}
              placeholder={selectedSubject === "demo" ? copy.openliftPlaceholder : copy.issPlaceholder}
              maxLength={160}
              aria-describedby={describe("extra", true)}
            />
            <span id="contact-form-extra-hint" className="contact-form__hint">
              {selectedSubject === "demo" ? copy.openliftHelp : copy.issHelp}
            </span>
            {errorFor("extra")}
          </div>
        )}

        <div className="contact-form__field">
          <label htmlFor="contact-form-message">{copy.message} <span className="contact-form__required" aria-hidden="true">*</span></label>
          <textarea {...commonInput("message")} rows={5} placeholder={copy.messagePlaceholder} maxLength={4000} required />
          {errorFor("message")}
        </div>

        {/* Existing privacy acknowledgement maintained pending legal review. */}
        <div className="contact-form__privacy">
          <div className="contact-form__consent">
            <input
              id="contact-form-consent"
              name="consent"
              type="checkbox"
              checked={values.consent}
              onChange={(event) => update("consent", event.target.checked)}
              onBlur={() => markTouched("consent")}
              ref={(node) => { inputRefs.current.consent = node; }}
              aria-invalid={Boolean((touched.consent || showErrors) && errors.consent)}
              aria-describedby={describe("consent")}
              disabled={status === "sending"}
              required
            />
            <label htmlFor="contact-form-consent">
              <Trans i18nKey="contact.consent_html" components={{ a: <a href="/politique-confidentialite" className="contact-form__privacyLink" /> }} />
            </label>
          </div>
          {errorFor("consent")}
          <p className="contact-form__privacyHint">{copy.helper}</p>
        </div>

        <div className="contact-form__footer">
          <span className="contact-form__requiredNote">{copy.requiredNote}</span>
          <button type="submit" className="contact-form__submit" disabled={status === "sending"} aria-busy={status === "sending"}>
            {status === "sending" ? copy.submitting : copy.submit}
            <span aria-hidden="true"> →</span>
          </button>
        </div>
      </form>
    </section>
  );
}
